import { Injectable, inject } from '@angular/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { storagePrefix } from '../utils/storage-key';
import { IdbStorageService, IDB_STORE_SIGNATURE } from './idb-storage.service';
import { PlatformService } from './platform.service';
import { DeviceCryptoService, isEncryptedPayload } from './device-crypto.service';

/**
 * Computes the entity-scoped `SecureStorage` key for a saved signature.
 *
 * @param entityId - Process server entity ID from the JWT.
 */
function esignKey(entityId: number): string {
  return `${storagePrefix(entityId)}_esign_data`;
}

/**
 * Persists the process server's reusable signature data URL so it survives app
 * restarts without a network round-trip.
 *
 * @remarks
 * **Storage backend is platform-dependent, deliberately (2026-08-05).**
 * - **Native:** `capacitor-secure-storage-plugin` — iOS Keychain / Android
 *   `KeyStore`-wrapped `SharedPreferences`. Unchanged, still plaintext at the
 *   JS layer — the OS itself is what encrypts it (confirmed by reading the
 *   plugin's own iOS/Android source: Keychain on iOS, an `AndroidKeyStore`
 *   RSA key on Android — see {@link DeviceCryptoService}'s doc comment for
 *   detail).
 * - **Web/PWA:** the IndexedDB `signature` store, AES-GCM–encrypted at rest
 *   (2026-08-05 follow-up — see below).
 *
 * **Web values are encrypted, not plaintext (2026-08-05 fix).** IndexedDB adds
 * no protection of its own — unlike native's OS-backed Keychain/Keystore, a
 * plaintext value here is readable by anyone who opens DevTools → Application
 * → IndexedDB, or pulls it from a browser-profile backup. `save()`/`get()` now
 * route through {@link DeviceCryptoService} (originally written just for this
 * class, since generalized for reuse by the affidavit-document encryption
 * too — see that service's own doc comment), which uses a device-bound,
 * non-extractable `CryptoKey` — see that service's doc comment for why that
 * key strategy (and not, say, reusing `CryptoService`'s build-embedded
 * `CRYPTOJSKEY`) is the one that actually adds protection rather than
 * decoration. A value written by a build between the IndexedDB move and this
 * encryption fix (plaintext string) is detected and transparently re-encrypted
 * the next time it's read — see {@link get}.
 *
 * A signature written by a pre-2026-08-05 web build (before signatures moved
 * off `localStorage` at all) is migrated out of the legacy
 * `capacitor-secure-storage-plugin` key on first read, and encrypted at that
 * point too.
 *
 * The key is entity-scoped so different users on the same device never
 * share signature data.  The same key is used by `SignAdapter` so both
 * the local cache and the API-sync path stay in sync automatically.
 *
 * Use `SignAdapter.saveEncryptedSignature()` separately when you also need
 * to persist the signature server-side (encrypted).
 *
 * @example
 * ```ts
 * await signatureStorage.save(entityId, dataUrl);
 * const saved = await signatureStorage.get(entityId); // string | null
 * await signatureStorage.clear(entityId);
 * ```
 */
@Injectable({ providedIn: 'root' })
export class SignatureStorageService {
  private readonly idb      = inject(IdbStorageService);
  private readonly platform = inject(PlatformService);
  private readonly crypto   = inject(DeviceCryptoService);

  /**
   * Stores the data URL under the entity-scoped key — in the Keychain/Keystore
   * on native, AES-GCM–encrypted in IndexedDB on web (see this class's doc
   * comment for why).
   *
   * @param entityId - Process server entity ID from the JWT.
   * @param dataUrl  - Base64 PNG data URL from the signature canvas.
   */
  async save(entityId: number, dataUrl: string): Promise<void> {
    const key = esignKey(entityId);
    if (this.platform.isNative) {
      await SecureStoragePlugin.set({ key, value: dataUrl });
      return;
    }
    try {
      const encrypted = await this.crypto.encrypt(dataUrl);
      await this.idb.set(IDB_STORE_SIGNATURE, key, encrypted);
    } catch {
      // IndexedDB (or Web Crypto) itself is unavailable — there is no other
      // place a non-extractable CryptoKey can be persisted across page loads,
      // so encryption isn't possible in this session. Falling back to the
      // same plaintext write pre-encryption builds used is still better than
      // losing the signature outright; both unavailable is swallowed since
      // there's nothing further this method can do.
      await SecureStoragePlugin.set({ key, value: dataUrl }).catch(() => { /* both unavailable */ });
    }
  }

  /**
   * Retrieves the stored signature data URL, decrypting it if needed, or
   * `null` when absent.
   *
   * @remarks
   * Handles three shapes the stored value can legitimately have, oldest first:
   * 1. **Legacy plaintext in `capacitor-secure-storage-plugin`** — a
   *    pre-2026-08-05 web build, before signatures moved to IndexedDB at all.
   * 2. **Plaintext string already in IndexedDB** — a build from the brief
   *    window between the IndexedDB move and this encryption fix.
   * 3. **An {@link EncryptedPayload}** — the current, correct shape.
   *
   * Cases 1 and 2 are self-healing: once found, the plaintext is immediately
   * re-saved through {@link save} (which always encrypts on web), so a
   * signature only ever needs to be caught unencrypted once, not on every
   * read.
   *
   * @param entityId - Process server entity ID from the JWT.
   * @returns The data URL string, or `null` on cold start, after clearing, or
   *          if a stored payload can no longer be decrypted (treated as
   *          absent rather than surfaced as an error — see
   *          `DeviceCryptoService.decrypt`'s doc comment).
   */
  async get(entityId: number): Promise<string | null> {
    const key = esignKey(entityId);
    if (this.platform.isNative) {
      try {
        const { value } = await SecureStoragePlugin.get({ key });
        return value || null;
      } catch {
        return null;
      }
    }

    const stored = await this.idb.get<unknown>(IDB_STORE_SIGNATURE, key);
    return stored !== null
      ? this.resolveIdbValue(entityId, stored)
      : this.migrateFromLegacyPlugin(entityId, key);
  }

  /**
   * Interprets a value already found in IndexedDB — either shape described in
   * {@link get}'s doc comment (an {@link EncryptedPayload}, or a plaintext
   * string from the brief pre-encryption window).
   *
   * @param entityId - Process server entity ID from the JWT.
   * @param stored - The raw value {@link get} already read from IndexedDB.
   */
  private async resolveIdbValue(entityId: number, stored: unknown): Promise<string | null> {
    if (isEncryptedPayload(stored)) {
      try {
        return (await this.crypto.decrypt(stored)) || null;
      } catch {
        return null; // Undecryptable — treat as absent, don't throw.
      }
    }
    if (typeof stored === 'string' && stored) {
      void this.save(entityId, stored); // Self-heal: encrypt for next time.
      return stored;
    }
    return null;
  }

  /**
   * Falls back to the legacy pre-2026-08-05 web location (before signatures
   * moved to IndexedDB at all) when nothing is found there.
   *
   * @param entityId - Process server entity ID from the JWT.
   * @param key - The entity-scoped storage key.
   */
  private async migrateFromLegacyPlugin(entityId: number, key: string): Promise<string | null> {
    let legacy: string | null = null;
    try {
      const { value } = await SecureStoragePlugin.get({ key });
      legacy = value || null;
    } catch {
      return null;
    }
    if (legacy) {
      void this.save(entityId, legacy); // Migrate AND encrypt in one step.
      try { await SecureStoragePlugin.remove({ key }); } catch { /* already gone */ }
    }
    return legacy;
  }

  /**
   * Removes the stored signature.
   * Call when the user explicitly discards their signature. Logout does not
   * call this — `AuthService.clearUserScopedKeys()` removes the same key
   * directly, and only when the user has no unsent work (2026-10-09).
   *
   * @remarks
   * Clears BOTH backends regardless of platform: a device that ran an earlier
   * build (or the opposite platform's code path) may hold a copy in the other
   * one.
   *
   * @param entityId - Process server entity ID from the JWT.
   */
  async clear(entityId: number): Promise<void> {
    await this.idb.removeEverywhere(IDB_STORE_SIGNATURE, esignKey(entityId));
  }
}
